import { usePwaInstallStore, isIos, isDesktopSafari } from '../../stores/pwaInstallStore'

interface Props {
  onClose: () => void
}

const step = (n: number) => (
  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-white">
    {n}
  </span>
)

// ponytail: single modal reused by the first-visit auto-banner and the
// manual "Install App" entry in Settings, so the two triggers can't drift apart.
export function InstallAppModal({ onClose }: Props) {
  const deferredPrompt = usePwaInstallStore((s) => s.deferredPrompt)
  const ios = isIos()
  const desktopSafari = isDesktopSafari()
  const manualInstructions = ios || desktopSafari

  const install = async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    await deferredPrompt.userChoice
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div className="w-full max-w-sm rounded-t-2xl bg-white p-6 shadow-xl sm:rounded-2xl">
        <h2 className="text-lg font-semibold text-gray-900">Install SnapShot</h2>
        <p className="mt-1 text-sm text-gray-600">
          Add SnapShot to your {desktopSafari ? 'Dock' : 'Home Screen'} for quick, full-screen access.
        </p>

        {ios ? (
          <ol className="mt-4 space-y-3 text-sm text-gray-700">
            <li className="flex items-center gap-3">
              {step(1)}
              Tap the <strong>Share</strong> icon
              <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M12 3v13m0-13 4 4m-4-4-4 4M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              in Safari's toolbar
            </li>
            <li className="flex items-center gap-3">
              {step(2)}
              Scroll down and tap <strong>Add to Home Screen</strong>
            </li>
            <li className="flex items-center gap-3">
              {step(3)}
              Tap <strong>Add</strong> to confirm
            </li>
          </ol>
        ) : desktopSafari ? (
          <ol className="mt-4 space-y-3 text-sm text-gray-700">
            <li className="flex items-center gap-3">
              {step(1)}
              Open the <strong>File</strong> menu in the Safari menu bar
            </li>
            <li className="flex items-center gap-3">
              {step(2)}
              Click <strong>Add to Dock…</strong>
              <span className="text-gray-400">(Safari 17+)</span>
            </li>
            <li className="flex items-center gap-3">
              {step(3)}
              Click <strong>Add</strong> to confirm
            </li>
          </ol>
        ) : !deferredPrompt ? (
          <p className="mt-4 text-sm text-gray-500">
            Your browser doesn't support one-tap install right now, or SnapShot is already installed.
          </p>
        ) : null}

        <div className="mt-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {manualInstructions ? 'Got it' : 'Not now'}
          </button>
          {!manualInstructions && deferredPrompt ? (
            <button
              onClick={install}
              className="flex-1 rounded-lg bg-primary-600 py-2.5 text-sm font-medium text-white hover:bg-primary-700"
            >
              Install
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
